// src/components/AllLessonsFrontend.js

import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api'; // Import put function
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { ArrowLeft, BookOpen, Play, Trophy, Zap } from 'lucide-react';
import useEarnedToday from './Sparks/earnedToday';
import useHomeRates from './Sparks/homeRates';
import HomeRatesTip from './Sparks/HomeRatesTip';
import AddyGuide from './homeLearning/AddyGuide';
import SectionTile from './homeLearning/SectionTile';
import RecommendedTile from './homeLearning/RecommendedTile';
import TopicBrowser from './homeLearning/TopicBrowser';
import { recommendLesson } from './homeLearning/recommend';
import { isPracticeExamTopic } from './homeLearning/lessonGroups';
import { playSelect, playStart } from '../utils/uiSounds';

// `tagline` is on the tile; `addyHover` is what Addy says while it is hovered; `addyOpen` once the section is open.
const SECTIONS = {
  topics: {
    title: 'Topics',
    tagline: 'Discover something new. Pick a topic and start exploring!',
    addyHover: "Ooh, let's learn something brand new!",
    addyOpen: 'Pick a topic you like the look of!',
    icon: BookOpen,
    colour: 'bg-nova-purple text-white',
  },
  coreSkills: {
    title: 'Core Skills',
    tagline: 'Power up your core skills with some quick practice!',
    addyHover: 'Quick practice makes your brain super strong! 💪',
    icon: Zap,
    colour: 'bg-nova-green text-[#2F1E6B]',
  },
  exams: {
    title: 'Practice Exams',
    tagline: 'Ready for a challenge? See how much you know!',
    addyHover: "Feeling brave? Let's see how much you know! 🏆",
    addyOpen: "Pick a practice exam. You've got this!",
    icon: Trophy,
    colour: 'bg-nova-coral text-[#2F1E6B]',
  },
};

const ADDY_CONTINUE_HOVER = "Let's jump back in!";
const ADDY_STARTING = "Woohoo! Let's go! 🚀";
const ADDY_RECOMMENDED_HOVER = 'I picked this one just for you! 🌟';

function AllLessonsFrontend() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [openSection, setOpenSection] = useState(null);
  // Addy's bubble: a hover line wins over the last thing picked, which wins over the greeting.
  const [addyHover, setAddyHover] = useState(null);
  const [addyNote, setAddyNote] = useState(null);
  const [celebrating, setCelebrating] = useState(false);
  const navigate = useNavigate();
  const { logout, loggedInChild, user } = useAuth();
  const earnedToday = useEarnedToday(loggedInChild?._id);
  const homeRates = useHomeRates(loggedInChild?._id);
  const ratesOn = !!homeRates?.enabled;

  useEffect(() => {
    fetchAllTopics();
    if (loggedInChild) {
      fetchCurrentTopic();
    }
  }, [loggedInChild]);

  const fetchAllTopics = async () => {
    try {
      const response = await get('/topics/visible');
      setTopics(response);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch topics');
      setLoading(false);
    }
  };

  const fetchCurrentTopic = async () => {
    try {
      const response = await get(`/children/${loggedInChild._id}/current-home-topic`, true);
      const topicId = response.homeCurrentTopicId?._id ?? response.homeCurrentTopicId;
      if (topicId) {
        const homeTrack = await get(`/childTopics/${loggedInChild._id}/${topicId}?track=home`, true);
        setCurrentTopic(homeTrack ? response.homeCurrentTopicId : null);
      } else {
        setCurrentTopic(null);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch current topic');
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    try {
      const response = await get(`/childTopics/${loggedInChild._id}/${currentTopic._id}?track=home`, true);
      const currentLesson = response.currentLesson;
      const learningStage = response.learningStage;

      if (learningStage === 'lesson') {
        if (!currentLesson) {
          confirmAlert({
            title: 'Lesson Not Found',
            message: 'The lesson no longer exists, please select another.',
            buttons: [
              {
                label: 'OK',
                onClick: async () => {
                  try {
                    // Delete the ChildTopic entry
                    await del(`/childTopics/${loggedInChild._id}/${currentTopic._id}?track=home`, true);
                    
                    // Clear the homeCurrentTopicId from the child document
                    await put(`/children/${loggedInChild._id}/current-home-topic`, { topicId: null }, true);
                    
                    fetchCurrentTopic();
                  } catch (error) {
                    console.error('Error clearing current topic or deleting ChildTopic entry:', error);
                  }
                }
              }
            ]
          });
          return;
        }

        const lessonExists = await get(`/lessons/${currentLesson._id}`);
        if (!lessonExists) {
          confirmAlert({
            title: 'Lesson Not Found',
            message: 'The lesson no longer exists, please select another.',
            buttons: [
              {
                label: 'OK',
                onClick: async () => {
                  try {
                    // Delete the ChildTopic entry
                    await del(`/childTopics/${loggedInChild._id}/${currentTopic._id}?track=home`, true);
                    
                    // Clear the homeCurrentTopicId from the child document
                    await put(`/children/${loggedInChild._id}/current-home-topic`, { topicId: null }, true);
                    
                    fetchCurrentTopic();
                  } catch (error) {
                    console.error('Error clearing current topic or deleting ChildTopic entry:', error);
                  }
                }
              }
            ]
          });
          return;
        }
        navigate(`/lesson/${currentLesson._id}`);
      } else if (learningStage === 'quiz') {
        const quizSubmissions = await get(`/quiz/quiz-submissions/search?childId=${loggedInChild._id}&topicId=${currentTopic._id}&lessonId=${currentLesson._id}&QuizStatus=Pending&isHomework=false&track=home&isForcedRed=false`, true);
        
        if (quizSubmissions && quizSubmissions.length > 0) {
          const quizSubmissionId = quizSubmissions[0]._id;
          navigate(`/quiz/${currentTopic._id}/${currentLesson._id}`, { state: { quizSubmissionId } });
        } else {
          console.log('No pending quiz submission found, creating a new one');
          try {
            // Create a new quiz submission
            const response = await post('/quiz/quiz-submissions', {
              lessonId: currentLesson._id,
              userId: user._id,
              childId: loggedInChild._id,
              topicId: currentTopic._id,
              QuizStatus: 'Pending',
              isCoreSkill: false,
              track: 'home',
              isForcedRed: false,
            }, true);
            
            // Navigate to quiz with the new submission
            navigate(`/quiz/${currentTopic._id}/${currentLesson._id}`, { state: { quizSubmissionId: response._id } });
          } catch (error) {
            console.error('Error creating new quiz submission:', error);
            confirmAlert({
              title: 'Error',
              message: 'Failed to create a quiz. Please try again or contact support.',
              buttons: [
                {
                  label: 'OK',
                  onClick: () => {}
                }
              ]
            });
          }
        }
      } else {
        console.error('Invalid learning stage:', learningStage);
        // Handle invalid learning stage
      }
    } catch (err) {
      confirmAlert({
        title: 'Error',
        message: 'Failed to continue. Please try again.',
        buttons: [
          {
            label: 'OK',
            onClick: () => {},
          },
        ],
      });
    }
  };

  const handleLessonClick = async (topicId, lessonId) => {
    playStart();
    try {
      await post('/childTopics/start-learning', { childId: loggedInChild._id, topicId, lessonId, track: 'home' }, true);
      await put(`/children/${loggedInChild._id}/current-home-topic`, { topicId }, true);
      navigate(`/lesson/${lessonId}`);
    } catch (err) {
      confirmAlert({
        title: 'Error',
        message: 'Failed to start learning. Please try again.',
        buttons: [
          {
            label: 'OK',
            onClick: () => {},
          },
        ],
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login'); // Redirect to login page after logout
  };

  if (!loggedInChild) {
    return (
      <div className="container mx-auto m-16">
        Please log in, using the <a href="/children-dashboard"><strong><u>children dashboard</u></strong></a> tab to start a lesson.
      </div>
    );
  }
  if (loading) return <p>Loading...</p>;
  if (error) return <p>{error}</p>;

  const learningTopics = topics.filter((topic) => !isPracticeExamTopic(topic));
  const examTopics = topics.filter(isPracticeExamTopic);
  // Practice Exams only shows when there is at least one visible diagnostic topic to sit.
  const sectionKeys = ['topics', 'coreSkills', ...(examTopics.length ? ['exams'] : [])];
  const recommendation = recommendLesson(learningTopics, ratesOn ? homeRates.lessons : null, earnedToday);

  const selectSection = (key) => {
    playSelect();
    setAddyHover(null);
    if (key === 'coreSkills') {
      navigate('/core-skills');
      return;
    }
    setOpenSection(key);
    setAddyNote(SECTIONS[key].addyOpen);
  };

  const backToSections = () => {
    setOpenSection(null);
    setAddyNote(null);
  };

  // Addy cheers while the lesson loads; if starting fails the page stays, so Addy stops.
  const cheerWhile = async (start) => {
    setAddyHover(null);
    setAddyNote(ADDY_STARTING);
    setCelebrating(true);
    await start();
    setCelebrating(false);
    setAddyNote(null);
  };

  const continueLearning = () => {
    playStart();
    cheerWhile(handleContinue);
  };

  const startLesson = (topicId, lessonId) => cheerWhile(() => handleLessonClick(topicId, lessonId));

  const hoverProps = (line) => ({ onHoverChange: (on) => setAddyHover(on ? line : null) });

  const greeting = currentTopic
    ? 'Welcome back! Ready to carry on?'
    : `Hi ${loggedInChild.name || 'there'}! What shall we do today?`;

  return (
    <div className="container mx-auto flex min-h-screen flex-col gap-6 p-4">
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h1 className="text-3xl font-extrabold text-[#2F1E6B]">Home Learning</h1>
        <AddyGuide childId={loggedInChild._id} message={addyHover ?? addyNote ?? greeting} talking={!!addyHover} celebrating={celebrating} />
      </header>

      {ratesOn && <HomeRatesTip childId={loggedInChild._id} />}

      {currentTopic ? (
        <div className="grid gap-4 md:grid-cols-2">
          <SectionTile
            title="Continue"
            tagline={currentTopic.topic ? `Jump back into ${currentTopic.topic}` : 'Jump back in where you left off'}
            icon={Play}
            colour="bg-nova-purple text-white"
            onClick={continueLearning}
            testId="home-continue"
            {...hoverProps(ADDY_CONTINUE_HOVER)}
          />
          <SectionTile
            {...SECTIONS.coreSkills}
            onClick={() => selectSection('coreSkills')}
            testId="home-section-coreSkills"
            {...hoverProps(SECTIONS.coreSkills.addyHover)}
          />
        </div>
      ) : openSection ? (
        <>
          <nav className="flex flex-wrap items-center gap-3 animate-in fade-in slide-in-from-top-4 duration-300 motion-reduce:animate-none" aria-label="Home Learning sections">
            <button
              type="button"
              onClick={backToSections}
              className="flex items-center gap-1 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#2F1E6B] shadow-sm transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-300"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              All sections
            </button>
            {sectionKeys.map((key) => (
              <SectionTile
                key={key}
                {...SECTIONS[key]}
                compact
                active={key === openSection}
                onClick={() => key !== openSection && selectSection(key)}
                testId={`home-section-${key}`}
              />
            ))}
          </nav>
          <TopicBrowser
            key={openSection}
            topics={openSection === 'exams' ? examTopics : learningTopics}
            childYearGroup={loggedInChild.yearGroup}
            emptyMessage="Nothing here yet. Check back soon!"
            earnedToday={earnedToday}
            homeRates={homeRates}
            ratesOn={ratesOn}
            onStartLesson={startLesson}
            onTopicSelect={(topic) => setAddyNote(`${topic.topic}! Great pick. Choose a lesson and press Let's go!`)}
          />
        </>
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          {recommendation && (
            <div className="md:col-span-3">
              <RecommendedTile
                recommendation={recommendation}
                onStart={() => startLesson(recommendation.topic._id, recommendation.lesson.lessonId._id)}
                {...hoverProps(ADDY_RECOMMENDED_HOVER)}
              />
            </div>
          )}
          {sectionKeys.map((key) => (
            <SectionTile
              key={key}
              {...SECTIONS[key]}
              onClick={() => selectSection(key)}
              testId={`home-section-${key}`}
              {...hoverProps(SECTIONS[key].addyHover)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default AllLessonsFrontend;
